import type { UseDisclosureReturn } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/withTranslate"
import { useEffect, useMemo, useState } from "react"
import { usePreviousDistinct } from "react-use"
import type {
  AugmentationToolbarHookReturn,
  AugmentationToolbarOption,
  AugmentationToolbarOptionNode,
  AugmentationToolbarOptions
} from "./augmentationToolbarTypes"

export function useOptionLabel(
  option?: AugmentationToolbarOptionNode | null
): string {
  const { t } = useTranslate()

  if (option) {
    if ("label" in option && option.label) {
      return option.label
    }
    if ("tLabel" in option && option.tLabel) {
      return typeof option.tLabel === "string"
        ? t(option.tLabel)
        : t(
            option.tLabel[0],
            option.tLabel[1] as string | undefined,
            option.tLabel[2] as Record<string, unknown> | undefined
          )
    }
  }

  return ""
}

export function useAugmentationToolbar(
  structuredOptions: AugmentationToolbarOptions
): AugmentationToolbarHookReturn {
  const [stack, setStack] = useState<AugmentationToolbarOptionNode[]>([])

  // Filter out all null options.
  const filteredStructuredOptions = useMemo(() => {
    return (
      structuredOptions
        // Filter out null options within groups.
        .map((option) =>
          Array.isArray(option) ? option.filter(Boolean) : option
        )
        // Filter out null or empty groups.
        .filter((option) =>
          Array.isArray(option) ? option.length > 0 : option
        ) as AugmentationToolbarOption[][]
    )
  }, [structuredOptions])

  const [options, optionsMap] = useMemo(() => {
    // Flatten the structured options into a single array of options (though these still
    // nest with children). We keep the structured options for rendering the tree.
    const options = filteredStructuredOptions.flatMap((option) =>
      Array.isArray(option) ? option : [option]
    ) as AugmentationToolbarOption[]

    const optionsMap: Map<string, AugmentationToolbarOptionNode> = new Map()

    // Add parent reference to children options for easier traversal up the tree.
    function extendOptions(
      options: AugmentationToolbarOptionNode[],
      parent: AugmentationToolbarOptionNode | null = null
    ): AugmentationToolbarOptionNode[] {
      // Embedded hosts may omit optional routes (e.g. Cover Page's legacy
      // management globals). Filter children too, before reading their keys.
      return options.filter(Boolean).map((option) => {
        // Create a composite key that includes parent keys for easier selection.
        const compositeKey = parent
          ? [parent.compositeKey, option.key].join("/")
          : option.key
        const newOption = { ...option, parent, compositeKey }
        // Side-effect: add to map as we go.
        optionsMap.set(compositeKey, newOption)

        if (newOption.children) {
          newOption.children = extendOptions(newOption.children, newOption)
        }
        return newOption
      })
    }

    const extendedOptions = extendOptions(options)

    return [extendedOptions as AugmentationToolbarOption[], optionsMap]
  }, [filteredStructuredOptions])

  function handleSelectKey(key: AugmentationToolbarOptionNode["key"] | null) {
    const option = optionsMap.get(key || "")

    // Get the stack of options from the selected option up to the root.
    function getStackFromOption(option: AugmentationToolbarOptionNode) {
      const stack = [option]
      while (stack[0]?.parent) {
        const parent = stack[0].parent
        // Add option to the start of the stack.
        stack.unshift(parent)
      }
      return stack
    }

    if (option?.AugmentationComponent) {
      setStack(getStackFromOption(option))
    } else {
      setStack([])
    }
  }

  const selectedOption = useMemo(() => stack.slice(-1)[0] || null, [stack])

  const selectedKey = selectedOption?.key || null

  return {
    stack,
    selectedKey,
    selectedOption,
    onSelect: handleSelectKey,
    options,
    structuredOptions: filteredStructuredOptions
  }
}

// Tweak the toolbar to include the disclosure button props.
export function useAugmentationDisclosedToolbar(
  toolbar: AugmentationToolbarHookReturn,
  disclosure: UseDisclosureReturn
): AugmentationToolbarHookReturn {
  // Clear the selected toolbar option, when the disclosure is closed
  // allowing the toolbar to once again show inline.
  const prevIsOpen = usePreviousDistinct(disclosure.open)
  useEffect(() => {
    if (prevIsOpen === true) toolbar.onSelect(null)
  }, [disclosure.open])

  useEffect(() => {
    // Open the disclosure when a toolbar option is selected.
    if (!disclosure.open && toolbar.selectedKey) disclosure.onOpen()
    // Close the disclosure when no toolbar option is selected.
    if (disclosure.open && !toolbar.selectedKey) disclosure.onClose()
  }, [toolbar.selectedKey])

  return {
    ...toolbar,
    options: toolbar.options.map((option) => ({
      ...option,
      onClick: (e) => {
        toolbar.onSelect(option.key)
        option.onClick?.(e as React.MouseEvent<HTMLButtonElement>)
      }
    }))
  }
}
